"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { LeadFormStepSchema } from "@shared/leadFormRules";
import type { RootState } from "@/store/store";
import { useNonVerifiedCapture } from "@/hooks/useNonVerifiedCapture";
import {
  getFormValuesFromCookie,
  isSameCourseAsSubmitted,
  isUserVerified as isVerifiedFromCookie,
  setFormValuesInCookie,
  setUserData,
} from "@/lib/userDataCookie";
import {
  setIsLeadSubmittedSuccessfully,
  setLeadDetailsAction,
} from "@/store/slices/globalslice";

/**
 * The visitor session a config-driven lead form takes part in.
 *
 * ─── Why this exists ────────────────────────────────────────────────────────────────
 * Every hand-built lead drop variant participates in a shared, cross-widget session: it
 * PREFILLS from the `userData` cookie, SHORT-CIRCUITS to the thank-you screen for someone
 * already verified, CAPTURES partial fills so an abandoned form still becomes a lead, and
 * WRITES the cookie back on success so the next widget the visitor meets knows them.
 *
 * `ConfigLeadForm` did none of those. A config-driven form was, as far as the rest of the
 * site was concerned, a stranger every time: it asked a verified visitor to verify again,
 * it never recorded that they had, so an inline form and a popup on the same page each
 * demanded the full journey, and an abandoned fill vanished instead of reaching the
 * non-verified sweep. This hook is variant 8's session behaviour (`useLeaddrop8WidgetState`
 * plus the cookie writes in `Leaddrop8.tsx`) expressed against a schema instead of against
 * a fixed set of fields.
 *
 * ─── Keyed by CATALOG, never by field id ────────────────────────────────────────────
 * The hand-built variants can say `formValues.phone` because their fields are hardcoded.
 * A config form's fields are author-named — the mobile question might have id `q1` on one
 * form and `mobile_no` on another. The catalog entry is the part the author cannot change,
 * so it is the only stable bridge between "what this form calls the field" and "what the
 * session calls the value". A form that collects none of these simply gets an empty seed.
 *
 * ─── Session SCOPE, and why the capture call belongs here ───────────────────────────
 * `useNonVerifiedCapture` mints one `capture_session_id` per MOUNT, and the server resolves
 * a capture by that id first. Held above the dialog — as variants 1, 7 and 8 still do — it
 * keeps resolving to the first open's row for the whole page lifetime, so a second open
 * with a different mobile overwrites the first person's lead. `ConfigLeadForm` is rendered
 * INSIDE `DialogContent`, which Radix unmounts on close, so calling the hook from here
 * gives one capture session per open — variant 9's (correct) shape rather than variant 8's.
 * That is the one place this deliberately does not copy form 8.
 */

/** Catalog id → the key the shared session/cookie stores that answer under. */
const CATALOG_TO_SESSION: Record<string, string> = {
  mobile: "phone",
  name: "name",
  email: "email",
  highest_qualification: "qualification",
  area_of_interest: "interest",
  state: "state",
  city: "city",
};

/** The session keys that carry an identity, in the order the payload builder wants them. */
export interface LeadFormSessionValues {
  phone: string;
  name: string;
  email: string;
  qualification: string;
  interest: string;
  course: string;
  [key: string]: unknown;
}

/** Which field id, if any, carries each catalog entry on this schema. */
const fieldIdsByCatalog = (steps: LeadFormStepSchema[]): Record<string, string> => {
  const out: Record<string, string> = {};
  for (const step of steps) {
    for (const f of Array.isArray(step.fields) ? step.fields : []) {
      // First wins: if an author has asked for the same concept twice, the earlier field is
      // the one on the earlier screen, which is the one worth prefilling.
      if (f?.catalog && !out[f.catalog]) out[f.catalog] = f.id;
    }
  }
  return out;
};

/** Answers → the flat session shape the cookie and the capture payload both speak. */
export const sessionValuesFromAnswers = (
  steps: LeadFormStepSchema[],
  answers: Record<string, unknown>,
): LeadFormSessionValues => {
  const ids = fieldIdsByCatalog(steps);
  const read = (catalog: string) => {
    const id = ids[catalog];
    const raw = id ? answers[id] : undefined;
    return raw === undefined || raw === null ? "" : String(raw);
  };
  const interest = read("area_of_interest");
  return {
    phone: read("mobile"),
    name: read("name"),
    email: read("email"),
    qualification: read("highest_qualification"),
    // `course` and `interest` are the same answer under two names. Both are written because
    // the legacy readers are split across them — `getSubmittedInterest` checks one, the
    // thank-you screens the other — and a form that filled only one would read as blank to
    // whichever reader it missed.
    interest,
    course: interest,
    state: read("state"),
    city: read("city"),
    // `media` / `campaign` are read from the URL exactly as variant 8 reads them — they are
    // plain query params, NOT the utm_* family, and the capture payload builder expects
    // them. "Direct" rather than "" is the fallback because that is the value every other
    // lead drop writes for an unattributed visit, and a blank here would read as missing
    // data rather than as a direct arrival.
    ...readMediaAndCampaign(),
  };
};

/** The two attribution params variant 8 carries on every lead. Safe on the server. */
const readMediaAndCampaign = (): { media: string; campaign: string } => {
  if (typeof window === "undefined") return { media: "Direct", campaign: "Direct" };
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return { media: "Direct", campaign: "Direct" };
  }
  return {
    media: (params.get("media") || "").trim() || "Direct",
    campaign: (params.get("campaign") || "").trim() || "Direct",
  };
};

export interface LeadFormSessionSeed {
  /** Prefill keyed by FIELD ID, ready to merge into `answers`. */
  answers: Record<string, unknown>;
  /** The visitor has already verified a mobile on this site. */
  verified: boolean;
}

const EMPTY_SEED: LeadFormSessionSeed = { answers: {}, verified: false };

/**
 * What the browser already knows about this visitor, mapped onto THIS schema's field ids.
 *
 * Returns an empty seed on the server. This is read in an effect rather than in a
 * `useState` initialiser on purpose: the embedded surface is server-rendered, and seeding
 * during the first render would make the server's empty inputs disagree with the client's
 * filled ones — a hydration mismatch, which React resolves by throwing the markup away.
 * One extra paint is the cost of not doing that.
 */
export const readLeadFormSession = (
  steps: LeadFormStepSchema[],
): LeadFormSessionSeed => {
  if (typeof window === "undefined") return EMPTY_SEED;

  let verified = false;
  try {
    verified =
      isVerifiedFromCookie() || sessionStorage.getItem("isUserVerified") === "true";
  } catch {
    /* Private mode / blocked storage — treat as a fresh visitor rather than crash. */
  }

  const cookie = getFormValuesFromCookie();
  if (!cookie) return { answers: {}, verified };

  const ids = fieldIdsByCatalog(steps);
  const pick = (...keys: string[]) => {
    for (const k of keys) {
      const raw = cookie[k];
      if (raw !== undefined && raw !== null && String(raw).trim() !== "") {
        return String(raw);
      }
    }
    return "";
  };

  const bySession: Record<string, string> = {
    phone: pick("phone", "mobile"),
    name: pick("name"),
    email: pick("email"),
    qualification: pick("qualification", "highest_qualification"),
    interest: pick("interest", "course"),
    state: pick("state"),
    city: pick("city"),
  };

  const seeded: Record<string, unknown> = {};
  for (const [catalog, fieldId] of Object.entries(ids)) {
    const sessionKey = CATALOG_TO_SESSION[catalog];
    if (!sessionKey) continue;
    const value = bySession[sessionKey];
    if (value) seeded[fieldId] = value;
  }
  return { answers: seeded, verified };
};

export interface UseLeadFormSessionArgs {
  steps: LeadFormStepSchema[];
  answers: Record<string, unknown>;
  /** Merge a prefill in without clobbering anything the visitor has already typed. */
  applySeed: (seed: Record<string, unknown>) => void;
  /** Called once if the browser says this visitor is already verified. */
  onVerifiedVisitor: () => void;
  /**
   * False in the admin preview and in the builder. Everything here has a side effect
   * outside the form — a cookie, a redux flag, a real `/leads/capture` row — and an author
   * clicking through their own draft must not create leads or convince the site they are
   * a verified visitor.
   */
  enabled: boolean;
  /**
   * The skin pins the area-of-interest question to the page's course. Only meaningful on a
   * course-bound page; elsewhere there is nothing to pin it to. See `bindsCourseToPage`.
   */
  bindCourseToPage?: boolean;
}

export interface LeadFormSessionApi {
  /** The session view of the current answers. Handy for analytics and for `persist`. */
  values: LeadFormSessionValues;
  /**
   * The question to pin to this page's course, and what to pin it to — null when the skin
   * does not bind, the page is about no single course, or the form never asks.
   *
   * Returned rather than applied here because `answers` belongs to the renderer: it is what
   * gets validated and submitted, so a pinned value has to live in it and not be painted on
   * at render time. The caller enforces it and disables the control.
   */
  courseBinding: { fieldId: string; value: string } | null;
  /**
   * Write the session back after a successful drop, so every other widget on the site —
   * and this form on the visitor's next page — treats them as known and verified.
   */
  persist: (details?: { leadId?: string | number | null; applicationRedirectUrl?: string }) => void;
}

export function useLeadFormSession({
  steps,
  answers,
  applySeed,
  onVerifiedVisitor,
  enabled,
  bindCourseToPage = false,
}: UseLeadFormSessionArgs): LeadFormSessionApi {
  const dispatch = useDispatch();
  const prefilled = useSelector(
    (state: RootState) => state.leaddrop?.defaultValues,
  );

  /**
   * The course this PAGE is about, if any — a programme or specialisation page binds one.
   * Same derivation as `LeaddropForm8`: the ERP unicode when there is one, else the id.
   */
  const pageCourse = useSelector(
    (state: RootState) =>
      state.globalslice?.course as { id?: unknown; erp_unicode?: unknown } | undefined,
  );
  const boundCourse = useMemo(() => {
    const erp = String(pageCourse?.erp_unicode ?? "").trim();
    if (erp) return erp;
    const id = pageCourse?.id != null ? String(pageCourse.id).trim() : "";
    return id || null;
  }, [pageCourse]);

  const values = useMemo(
    () => sessionValuesFromAnswers(steps, answers),
    [steps, answers],
  );

  /**
   * The latest answers, readable from a STABLE callback.
   *
   * `persist` is called from inside `submitLead`, which several other callbacks depend on.
   * Closing over `answers` directly would rebuild that whole chain on every keystroke —
   * and, worse, a `persist` captured mid-journey would write the answers as they were when
   * it was created rather than as they are when the lead is actually delivered.
   */
  const latest = useRef(values);
  latest.current = values;

  /**
   * Record-only partial capture. Passing `null` while disabled is what switches it off:
   * the hook activates on a valid phone alone, so an author typing their own number into
   * a preview would otherwise mint a real lead row and arm the non-verified sweep.
   */
  useNonVerifiedCapture(enabled ? values : null);

  /* ── Seed once, from the cookie ─────────────────────────────────────────────────── */
  const seeded = useRef(false);
  useEffect(() => {
    if (!enabled || seeded.current) return;
    // Wait for the schema. A form whose config is still loading has no field ids to map
    // onto, and seeding an empty map would latch the ref and lose the prefill entirely.
    if (steps.length === 0) return;
    seeded.current = true;
    const session = readLeadFormSession(steps);

    /**
     * A COURSE-BOUND page is the exception to the verified short-circuit.
     *
     * Being verified means "we have this person's number", not "we have this enquiry". A
     * visitor who registered for one programme and then opens the page for another is a new
     * enquiry, and showing them "welcome back" instead of a form loses it outright — the
     * university never learns they were interested in the second course. Variant 8 draws
     * exactly this distinction (`shouldSkipSubmitAndShowThankYou` / `lockEnrollmentCourse`):
     * short-circuit only when the stored enquiry is for the course in front of them.
     *
     * A page not about a single course has nothing to compare, so the plain rule applies.
     */
    const reEnquiring = boundCourse !== null && !isSameCourseAsSubmitted(boundCourse);

    const seed = { ...session.answers };
    if (boundCourse !== null) {
      // Bind the interest to THIS page's course. Without this the cookie would prefill the
      // previously-submitted programme and the re-enquiry would be filed against the wrong
      // course — worse than not prefilling at all.
      const aoiFieldId = fieldIdsByCatalog(steps).area_of_interest;
      if (aoiFieldId) seed[aoiFieldId] = boundCourse;
    }

    if (Object.keys(seed).length > 0) applySeed(seed);
    if (session.verified && !reEnquiring) onVerifiedVisitor();
  }, [enabled, steps, applySeed, onVerifiedVisitor, boundCourse]);

  /* ── Seed again if the store hands us values later ──────────────────────────────── */
  useEffect(() => {
    if (!enabled || !prefilled || steps.length === 0) return;
    const ids = fieldIdsByCatalog(steps);
    const patch: Record<string, unknown> = {};
    const put = (catalog: string, raw: unknown) => {
      const id = ids[catalog];
      if (!id || raw === undefined || raw === null || String(raw).trim() === "") return;
      patch[id] = String(raw);
    };
    const p = prefilled as Record<string, unknown>;
    put("mobile", p.phone ?? p.mobile);
    put("name", p.name);
    put("email", p.email);
    put("highest_qualification", p.qualification ?? p.highest_qualification);
    // Never let the store's remembered interest land on a course-bound page. The seeding
    // effect above binds this field to the page's course, but it waits for the schema — so
    // if the config arrives late this effect can run first, and a blank-filling seed cannot
    // undo it afterwards. Variant 8 guards the same way (`hasForcedEnrollmentCourse`).
    if (boundCourse === null) put("area_of_interest", p.interest ?? p.course);
    if (Object.keys(patch).length > 0) applySeed(patch);
  }, [enabled, prefilled, steps, applySeed, boundCourse]);

  /* ── Write the session back on success ──────────────────────────────────────────── */
  const persist = useCallback<LeadFormSessionApi["persist"]>(
    (details) => {
      if (!enabled || typeof window === "undefined") return;
      const v = latest.current;
      const redirect = String(details?.applicationRedirectUrl ?? "").trim();

      const formValues: Record<string, unknown> = {
        name: v.name,
        email: v.email,
        phone: v.phone,
        qualification: v.qualification,
        interest: v.interest || v.course,
        course: v.course || v.interest,
        ...(redirect ? { applicationRedirectUrl: redirect } : {}),
      };

      try {
        setFormValuesInCookie(formValues);
        setUserData({
          isVerified: true,
          isUserVerified: true,
          ...(details?.leadId != null ? { leads_id: String(details.leadId) } : {}),
          ...(v.interest ? { area_of_interest: v.interest, interest: v.interest } : {}),
          ...(v.qualification ? { highest_qualification: v.qualification } : {}),
          ...(redirect ? { applicationRedirectUrl: redirect } : {}),
          formValues,
        });
        sessionStorage.setItem("isUserVerified", "true");
        if (redirect) sessionStorage.setItem("leadApplicationRedirectUrl", redirect);
      } catch {
        /* Blocked storage. The lead is already delivered; the session is a convenience. */
      }

      // `lead_submitted_successfully` is also what stops `useNonVerifiedCapture` above:
      // a completed lead must not keep re-arming the non-verified sweep, or the 15-minute
      // job would drop it a second time as a duplicate "Re-Enquired".
      dispatch(setIsLeadSubmittedSuccessfully(true));
      dispatch(setLeadDetailsAction({ isExistingLead: true }));
    },
    [enabled, dispatch],
  );

  const courseBinding = useMemo(() => {
    if (!bindCourseToPage || !boundCourse || steps.length === 0) return null;
    const fieldId = fieldIdsByCatalog(steps).area_of_interest;
    // A form that never asks about the course has nothing to pin — that is a normal
    // shape (a phone-only journey), not a misconfiguration.
    if (!fieldId) return null;
    /**
     * This is the INTENT to pin, not the decision. Whether the field is actually locked is
     * settled by the caller against the question's loaded options: a course the question
     * does not offer must leave the field open rather than lock it to something the author
     * never listed. See `lockedCourse` in ConfigLeadForm.
     */
    return { fieldId, value: boundCourse };
  }, [bindCourseToPage, boundCourse, steps]);

  return useMemo(
    () => ({ values, persist, courseBinding }),
    [values, persist, courseBinding],
  );
}
